import { useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';

export type DrawingCompleteCallback = (polygon: google.maps.Polygon) => void;

export interface DrawingController {
  isDrawing: boolean;
  startDrawing: () => void;
  stopDrawing: () => void;
  onPolygonComplete: (callback: DrawingCompleteCallback) => () => void;
}

export function useDrawingManager(): DrawingController {
  const map = useMap();
  const [isDrawing, setIsDrawing] = useState(false);

  const pointsRef = useRef<google.maps.LatLngLiteral[]>([]);
  const previewPolygonRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const callbacksRef = useRef<DrawingCompleteCallback[]>([]);
  // flag to swallow the click that fires as part of a dblclick
  const ignoringNextClickRef = useRef(false);

  const completePolygon = useCallback(() => {
    if (pointsRef.current.length < 3 || !map) return;

    if (previewPolygonRef.current) {
      previewPolygonRef.current.setMap(null);
      previewPolygonRef.current = null;
    }

    const polygon = new google.maps.Polygon({
      paths: [...pointsRef.current],
      map,
      editable: true,
      draggable: true,
    });

    pointsRef.current = [];
    setIsDrawing(false);
    map.setOptions({ disableDoubleClickZoom: false });

    callbacksRef.current.forEach(cb => cb(polygon));
  }, [map]);

  const stopDrawing = useCallback(() => {
    listenersRef.current.forEach(l => google.maps.event.removeListener(l));
    listenersRef.current = [];

    if (previewPolygonRef.current) {
      previewPolygonRef.current.setMap(null);
      previewPolygonRef.current = null;
    }

    pointsRef.current = [];
    ignoringNextClickRef.current = false;
    setIsDrawing(false);
    if (map) map.setOptions({ disableDoubleClickZoom: false });
  }, [map]);

  const startDrawing = useCallback(() => {
    if (!map) return;
    stopDrawing();
    setIsDrawing(true);
    map.setOptions({ disableDoubleClickZoom: true });

    const clickListener = google.maps.event.addListener(map, 'click', (e: google.maps.MapMouseEvent) => {
      if (ignoringNextClickRef.current) {
        ignoringNextClickRef.current = false;
        return;
      }
      if (!e.latLng) return;
      pointsRef.current = [...pointsRef.current, { lat: e.latLng.lat(), lng: e.latLng.lng() }];

      if (!previewPolygonRef.current) {
        previewPolygonRef.current = new google.maps.Polygon({
          map,
          editable: false,
          draggable: false,
          clickable: false,
          fillOpacity: 0.35,
          strokeColor: '#1a73e8',
          fillColor: '#1a73e8',
          strokeWeight: 2,
        });
      }
      previewPolygonRef.current.setPath(pointsRef.current);
    });

    const dblClickListener = google.maps.event.addListener(map, 'dblclick', () => {
      // dblclick fires after a single click event for the second tap — ignore that click
      ignoringNextClickRef.current = true;
      // remove the point added by the first tap of the double-click
      pointsRef.current = pointsRef.current.slice(0, -1);

      listenersRef.current.forEach(l => google.maps.event.removeListener(l));
      listenersRef.current = [];

      completePolygon();
    });

    listenersRef.current = [clickListener, dblClickListener];
  }, [map, stopDrawing, completePolygon]);

  const onPolygonComplete = useCallback((callback: DrawingCompleteCallback) => {
    callbacksRef.current = [...callbacksRef.current, callback];
    return () => {
      callbacksRef.current = callbacksRef.current.filter(cb => cb !== callback);
    };
  }, []);

  useEffect(() => {
    return () => stopDrawing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isDrawing, startDrawing, stopDrawing, onPolygonComplete };
}
